import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { addMinutes } from 'date-fns';
import { uniqBy } from 'lodash-es';
import { TranslateService } from '@ngx-translate/core';

import { Events } from '@omni/events';
import { DiskService } from '@omni/services/disk/disk.service';
import { AgendaFooterService } from '@omni/services/footer/agenda-footer.service';
import { EntityNameForTranslation, LocalizationService } from '@omni/services/localization/localization.service';
import { SecInfoConfigDataService } from '@omni/data-services/sec-info-config/sec-info-config-data-service';

import { AppointmentActivity } from '../../classes/activity/appointment.activity.class';
import { EmailActivityParty, EmailAddress, EmailViewType } from '../../classes/activity/email.activity.class';
import { ChannelActivityType, EmailActivityChannelDetails } from '../../classes/consent/channel.class';
import { EmailTemplateType } from '../../classes/email-templates/email-template.class';
import { CreateSampleDropRequestBody, SampleActivity } from '../../classes/activity/sample.activity.class';
import { EmailService } from '../email-templates/email.service';
import { FooterService, FooterViews } from '../footer/footer.service';
import { SampleDataService } from '../../data-services/sample/sample.data.service';
import { SampleService, SamplingDetailsViewMode } from '../sample/sample.service';
import { InitiatePhoneCallPayload, PhoneCallDataService } from '../../data-services/phone-call/phonecall.data.service';
import { OFFLINE_PHONE_CALL_ID_PREFIX, PhoneActivity } from '../../classes/activity/phone.activity.class';
import { PhoneCallMeetingStructureService } from '../phone-call-meeting-structure/phone-call-meeting-structure.service';
import { FOLLOW_UP_TYPE, FollowUpActivity } from '../../classes/activity/follow-up-action.activity.class';
import { FollowUpActivityDataService } from '../../data-services/follow-up-activity/follow-up-activity.data.service';
import { AccesingMode, CaseManagementService } from '../case-management/case-management.service';
import { CaseActivity } from '../../classes/case-intake/case-activity.class';
import { CaseType } from '../../classes/case-intake/case-utility.class';
import { SurgeryOrderActivity } from '../../classes/activity/surgery-order.activity.class';
import { OrderActivity } from '../../classes/activity/order.activity.class';
import { OrderActivityDataService } from '../../data-services/order-activity/order-activity.data.service';
import { StoreCheckService } from '../store-check/store-check.service';
import { DynamicFormsService } from '../dynamic-forms/dynamic-forms-service';
import { XperiencesService } from '../xperiences/xperiences.service';
import { TrendingCustomer } from '../../classes/xperiences/trending.customer.class';
import { FormType } from '../../classes/dynamic-form/dynamic-form.class';
import { SurgeryOrderActivityDataService } from '../../data-services/surgery-order-activity/surgery-order-activity.data.service';
import { ProcedureTrackerActivity } from '../../classes/activity/procedure-tracker.activity.class';
import { ProcedureTrackerActivityDataService } from '../../data-services/procedure-tracker-activity/procedure-tracker-activity.data.service';
import { DateTimeFormatsService } from '../date-time-formats/date-time-formats.service';
import { Utility } from '../../utility/util';
import { Guid } from 'typescript-guid';
import { Activity, FormatType, MeetingActivityType, MeetingActivityTypeCode } from '../../classes/activity/activity.class';
import { FeatureActionsMap, IoConfigurationFeatureCodes } from '../../classes/authentication/user.class';
import { ActivityDataService } from '../../data-services/activity/activity.service';
import { InitiateMeetingPayload, MeetingDataService, UpdateMeetingPayload } from '../../data-services/meeting/meeting.data.service';
import { shouldSkipPostCreateMeetingPatch } from '../../data-services/meeting/meeting-create-fast-path';
import { ActivityService } from './activity.service';
import { AuthenticationService } from '../authentication.service';
import { DeviceService } from '../device/device.service';
import { MeetingStructureService } from '../meeting-structure/meeting-structure.service';
import { ChildNavNames, NavigationService, PageName } from '../navigation/navigation.service';
import { NotificationService, ToastStyle } from '../notification/notification.service';
import { TrackService, TrackingEventNames } from '../logging/tracking.service';
import { KitBookingFormService } from '../kit-booking/kit-booking-form.service';
import { UIService } from '../ui/ui.service';
import { Contact } from '../../classes/contact/contact.class';
import { Account } from '../../classes/account/account.class';
// Type only: the strip service imports this one, so a value import here would make a cycle.
import type { QuickActionSource } from './quick-action-strip.service';
import { ContactOfflineService } from '../contact/contact.service';
import { AccountOfflineService } from '../account/account.offline.service';
import { ContentMatchingService } from '../content-matching/content-matching.service';
import { ActivityType } from '../../classes/activity/activity.class';
import { TimeOffDataService } from '../../data-services/time-off/time-off.data.service';
import { TimeOffService } from '../time-off/time-off.service';
import { TimeOffUtilityService } from '../time-off/time-off-utility.service';
import type { IndActionMenuOption } from '@omni/components/shared/ind-action-menu/ind-action-menu';
import type { KnownCustomer } from '../voice-note/dictation-resolution';

export type QuickActivityType = 'meeting' | 'kitBooking' | 'message' | 'allocationOrder' | 'phoneCall'
  | 'followUpAction' | 'customerInquiry' | 'procedureLog' | 'accountVisit' | 'salesOrder' | 'storeCheck' | 'medicalInquiry' | 'customerInsight' | 'bulkProcedureLog'
  | 'timeOff';

/** Bulk Procedure Log is a separate entity, created by its own quick activity type. */
export type ProcedureLogCategory = 'procedureDay' | 'noProcedureDay';
export const BULK_PROCEDURE_LOG = 'procedure_tracker';
export type ProcedureLogOption = ProcedureLogCategory | typeof BULK_PROCEDURE_LOG;
/** Longest a page hand-off holds the create guard; a real push lands well inside it. */
const HAND_OFF_GUARD_MAX_MS = 2000;
/** In the order a new phone call picks its number: the first one the contact has. */
export const PHONE_NUMBER_FIELDS = ['mobilePhone', 'indskr_alternatephone1', 'indskr_alternatephone2', 'indskr_alternatephone3', 'telephone'];

/**
 * The customer the activity is being created for, and the tool the rep is standing in.
 *
 * Absent means the top bar: a bare record that re-roots to the Activities tool. Present means
 * a customer surface, which both fills the record in — attendee, subject, location, affiliated
 * accounts — and keeps the rep where they are.
 */
export interface QuickCreateContext {
  contact?: Contact;
  account?: Account;
  /** Tool whose right pane hosts the new record. Absent re-roots to the Activities tool. */
  host?: PageName;
  /** activeView the host wants back when the pushed pane closes; it reads uiService.prevView. */
  restoreView?: string;
}

export interface QuickCreateOptions {
  meetingFormat?: MeetingActivityType;
  messageChannel?: EmailActivityChannelDetails;
  caseType?: CaseType;
  procedureLogCategory?: ProcedureLogCategory;
  followUpCategory?: FOLLOW_UP_TYPE;
  context?: QuickCreateContext;
  /**
   * Request First Time Email: the message is addressed to a contact who has not consented
   * yet, so the channel credential check and the do-not-email read are skipped and the
   * record is typed so the template picker offers first-time templates only.
   */
  firstTimeEmail?: boolean;
}

export interface QuickCreateResult {
  created: boolean;
  /** Kit Booking opens a form instead of creating; absence of a record is not failure. */
  formOpened?: boolean;
  activity?: Activity;
  /** Translation key of the refusal, or undefined when the gate toasted for itself. */
  refusedReason?: string;
}

const REFUSED = (refusedReason?: string): QuickCreateResult => ({ created: false, refusedReason });

function knownCustomerFrom(context?: QuickCreateContext): KnownCustomer | null {
  if (context?.contact) return { kind: 'contact', record: { id: context.contact.ID, name: context.contact.fullName } };
  if (context?.account) return { kind: 'account', record: { id: context.account.id, name: context.account.accountName } };
  return null;
}

/** What create() refuses each online-only type with, so a surface can grey the type out first. */
const OFFLINE_REFUSALS: Partial<Record<QuickActivityType, string>> = {
  kitBooking: 'DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED',
  customerInsight: 'DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED',
  storeCheck: 'STORE_CHECK_OFFLINE',
  bulkProcedureLog: 'DEVICE_OFFLINE_FEATURE_NOT_SUPPORTED',
  // Toasted by ActivityService.accountVisitOfflineCheck, which createAccountVisit defers to.
  accountVisit: 'ACCOUNT_VISIT_OFFLINE_WARNING',
};

/** What each channel needs on the contact before a message can be addressed to them. */
const MISSING_CHANNEL_CREDENTIAL: Record<string, (contact: Contact) => string | null> = {
  [ChannelActivityType.EMAIL]: c => c.emailAddressList?.length ? null : 'NO_EMAIL_ADDRESS_ASSOCIATED',
  [ChannelActivityType.FACEBOOK]: c => c.indskr_facebookpsid ? null : 'NO_FACE_ID_ASSOCIATED',
  [ChannelActivityType.SMS]: c => (c.indskr_whatsappid || c.mobilePhone) ? null : 'NO_MOBILE_NUMBER_ASSOCIATED',
  [ChannelActivityType.WHATSAPP]: c => (c.indskr_whatsappid || c.mobilePhone) ? null : 'NO_MOBILE_NUMBER_ASSOCIATED',
};

/**
 * Tools that keep their right-pane nav hidden until uiService.activeView names it, so
 * their own views and the pushed page never render at once. The detail pane puts the
 * previous value back when it closes.
 */
const ACTIVE_VIEW_GATED_TOOLS = new Map<PageName, string>([
  [PageName.AllocationComponent, ChildNavNames.CustomerAllocationsToolNavigation],
  [PageName.CaseManagementHomePage, ChildNavNames.CaseManagementNavigation],
  [PageName.TimeOffComponent, ChildNavNames.TimeOffNavigation],
]);

/** Full-screen tools with no right pane to host the meeting; it opens in Activities instead. */
const TOOLS_WITHOUT_A_RIGHT_PANE = new Set<PageName>([
  PageName.ExternalAppFrameComponent,
]);

@Injectable({ providedIn: 'root' })
export class ActivityCreationService {

  private readonly _inFlight = new BehaviorSubject<boolean>(false);
  /** Every surface that can start a create subscribes: one tap at a time, across components. */
  public readonly inFlight$ = this._inFlight.asObservable();

  /** What the host tool was showing before the currently-open pane took its right pane. */
  private _paneRestoreContext: {
    activeView: string;
    activity: any;
    showRightPane: boolean;
    childNavRightPane: boolean;
  } | null = null;

  constructor(
    private readonly activityService: ActivityService,
    private readonly activityDataService: ActivityDataService,
    private readonly agendaFooterService: AgendaFooterService,
    private readonly authService: AuthenticationService,
    private readonly device: DeviceService,
    private readonly diskService: DiskService,
    private readonly emailService: EmailService,
    private readonly footerService: FooterService,
    private readonly sampleDataService: SampleDataService,
    private readonly sampleService: SampleService,
    private readonly phoneCallDataService: PhoneCallDataService,
    private readonly phoneCallMeetingStructureService: PhoneCallMeetingStructureService,
    private readonly followUpActivityDataService: FollowUpActivityDataService,
    private readonly caseManagementService: CaseManagementService,
    private readonly surgeryOrderActivityDataService: SurgeryOrderActivityDataService,
    private readonly procedureTrackerActivityDataService: ProcedureTrackerActivityDataService,
    private readonly orderActivityDataService: OrderActivityDataService,
    private readonly storeCheckService: StoreCheckService,
    private readonly dynamicFormService: DynamicFormsService,
    private readonly xperiencesService: XperiencesService,
    private readonly dateTimeFormatsService: DateTimeFormatsService,
    private readonly events: Events,
    private readonly localizationService: LocalizationService,
    private readonly meetingService: MeetingDataService,
    private readonly meetingStructureService: MeetingStructureService,
    private readonly navService: NavigationService,
    private readonly notificationService: NotificationService,
    private readonly kitBookingFormService: KitBookingFormService,
    private readonly secondaryInfoService: SecInfoConfigDataService,
    private readonly trackingService: TrackService,
    private readonly translate: TranslateService,
    private readonly uiService: UIService,
    private readonly contactService: ContactOfflineService,
    private readonly accountService: AccountOfflineService,
    private readonly contentMatchingService: ContentMatchingService,
    private readonly timeOffDataService: TimeOffDataService,
    private readonly timeOffService: TimeOffService,
    private readonly timeOffUtilityService: TimeOffUtilityService,
    private readonly injector: Injector,
  ) { }

  /** For hand-offs that push a page instead of creating: shares create's guard, so a double tap pushes once. */
  public async runExclusive(handOff: () => Promise<unknown>): Promise<void> {
    if (this._inFlight.value) return;
    this._inFlight.next(true);
    try {
      // ion-nav drops queued pushes without settling them when a transition fails; unbounded,
      // that would leave every create refused until restart.
      const guardReleased = new Promise(resolve => setTimeout(resolve, HAND_OFF_GUARD_MAX_MS));
      await Promise.race([handOff(), guardReleased]);
    } finally {
      this._inFlight.next(false);
    }
  }

  public async create(type: QuickActivityType, options: QuickCreateOptions = {}): Promise<QuickCreateResult> {
    if (this._inFlight.value) return REFUSED();
    this._inFlight.next(true);
    // Read once: the loader is a LIFO stack, so if connectivity flips mid-create an
    // unpaired display or dismiss desyncs it for whoever else is using it.
    const usingLoader = !this.device.isOffline;
    if (usingLoader) this.uiService.displayLoader();

    // Captured before the create overwrites it: the tool underneath may be rendering
    // its own right pane off selectedActivity, and closing must hand it back.
    const displacedActivity = this.activityService.selectedActivity;

    try {
      switch (type) {
        case 'kitBooking': return await this.openKitBookingForm();
        case 'message': return await this.createMessage(options.messageChannel, options.context, options.firstTimeEmail);
        case 'allocationOrder': return await this.createAllocationOrder(options.context);
        case 'phoneCall': return await this.createPhoneCall(options.context);
        case 'followUpAction': return await this.createFollowUpAction(options.context, options.followUpCategory);
        case 'customerInquiry': return await this.createCustomerInquiry(options.caseType, options.context);
        case 'procedureLog': return await this.createProcedureLog(options.procedureLogCategory, options.context);
        case 'accountVisit': return await this.createAccountVisit(options.context);
        case 'salesOrder': return await this.createSalesOrder(options.context);
        case 'storeCheck': return await this.createStoreCheck(options.context);
        case 'medicalInquiry': return await this.openMedicalInquiryForm(options.context);
        case 'customerInsight': return await this.openCustomerInsight();
        case 'bulkProcedureLog': return await this.createBulkProcedureLog(options.context);
        case 'timeOff': return await this.createTimeOff();
        default: return await this.createMeeting(options.meetingFormat, displacedActivity, options.context);
      }
    } catch (error) {
      console.error('ActivityCreationService: create failed', error);
      // Only the meeting path touches the shared structure subject; blanking it for the
      // others would wipe the strip of whatever meeting is still selected.
      if (type === 'meeting') this.meetingStructureService.createEmbeddedInteractions(null);
      return this.fail('ERROR_TRYAGAIN');
    } finally {
      if (usingLoader) this.uiService.dismissLoader();
      this._inFlight.next(false);
    }
  }

  /**
   * Kit Booking writes nothing here — it opens a form the rep saves. Like the meeting it
   * lands in the Activities tool, so the form service's own Activities branch hosts it.
   */
  private async openKitBookingForm(): Promise<QuickCreateResult> {
    if (this.device.isOffline) {
      return this.fail(OFFLINE_REFUSALS.kitBooking, ToastStyle.INFO);
    }

    await this.enterActivitiesTool();
    // The form service's Activities branch assumes the rep is already on a right pane that
    // activeView names; arriving from another tool it still names that tool's view.
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';

    // Scope 1: hosting the form in the tool the rep started from is parked, not dropped.
    // await this.kitBookingFormService.openCreateForm({
    //   stayInTool: true,
    //   masterPage: this.navService.getCurrentMasterPageName(),
    // });
    await this.kitBookingFormService.openCreateForm();
    this.uiService.showRightPane = true;
    return { created: false, formOpened: true };
  }

  /**
   * The agenda branch of NewActivityComponent's 'new-insight' case. Writes nothing — it opens
   * the insight page. The New Activity row has no type picker and leaves selectedInsightType
   * at its CUSTOMER_INSIGHT default, so this does the same; General Insight stays behind `+`.
   */
  private async openCustomerInsight(): Promise<QuickCreateResult> {
    if (this.device.isOffline) {
      return this.fail(OFFLINE_REFUSALS.customerInsight, ToastStyle.INFO);
    }

    await this.enterActivitiesTool();
    // An insight is not an activity: the source path clears the selection rather than
    // setting one, so the agenda does not keep a record highlighted behind the form.
    this.activityService.selectedActivity = null;
    this.activityService.selectedActivityAtHome = null;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    this.uiService.showNewActivity = false;
    await this.navService.setChildNavRoot(
      PageName.NothingSelectedView,
      PageName.NothingSelectedView,
      PageName.GoAppActivitiesPageComponent,
    );
    this.xperiencesService.newInsight = new TrendingCustomer();
    await this.navService.pushChildNavPageWithPageTracking(
      PageName.CustomerInsightComponent,
      PageName.CustomerInsightComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppActivitiesPageComponent },
    );
    this.uiService.showRightPane = true;
    return { created: false, formOpened: true };
  }

  /**
   * The home branch of NewActivityComponent.handleNewMedicalInquiry. Like Kit Booking this
   * writes nothing — it opens a form the rep saves — and it is the only type that can refuse
   * for missing configuration rather than missing data.
   *
   * No expert-category requirement: MI has no functional association with them, so the form opens
   * on the FA alone (see the New Activity row). A host context keeps the form in that tool's right
   * pane with the customer pre-selected, mirroring createCustomerInquiry; without one it re-roots to
   * the Activities tool.
   */
  /** MedicalInquiryDetailComponent maps this marker back to a master page, so
   *  it must name the host actually launched from. Unmapped hosts return
   *  undefined and leave the detail view on its own default. */
  private _launchedFrom(host: PageName): string | undefined {
    return host === PageName.ContactPageComponent ? 'ContactTool' : undefined;
  }

  private async openMedicalInquiryForm(context?: QuickCreateContext): Promise<QuickCreateResult> {
    if (!this.dynamicFormService.getFormDefinitionForEntity('indskr_medicalinquiry', FormType.CREATEFORM)) {
      return this.fail('MI_NO_CREATE_FORM_CONFIGURED');
    }

    this.uiService.showNewActivity = false;
    this.uiService.prevView = this.uiService.activeView;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      await this.navService.pushChildNavPageWithPageTracking(
        PageName.MedicalInquiryNewComponent,
        PageName.MedicalInquiryNewComponent,
        context.host,
        { from: this._launchedFrom(context.host), contact: context.contact },
      );
      return { created: false, formOpened: true };
    }

    await this.enterActivitiesTool();
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    // The source path pushes onto a NothingSelectedView root so Back lands on the empty
    // state rather than on New Activity; from here the root switch already did that.
    await this.navService.setChildNavRoot(
      PageName.NothingSelectedView,
      PageName.NothingSelectedView,
      PageName.GoAppActivitiesPageComponent,
    );
    await this.navService.pushChildNavPageWithPageTracking(
      PageName.MedicalInquiryNewComponent,
      PageName.MedicalInquiryNewComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: 'NewActivity' },
    );
    this.uiService.showRightPane = true;
    return { created: false, formOpened: true };
  }

  /**
   * The agenda branch of NewActivityComponent.createStoreCheck. The store check service
   * stamps the owner and adds the record to the agenda itself, so there is nothing to
   * assign after construction. Online only.
   */
  private async createStoreCheck(context?: QuickCreateContext): Promise<QuickCreateResult> {
    if (this.device.isOffline) {
      return this.fail(OFFLINE_REFUSALS.storeCheck);
    }

    const start = new Date();
    const isPromoCheck = this.authService.hasFeatureAction(FeatureActionsMap.PROMO_CHECK);
    const payload: any = {
      scheduledstart: start.getTime(),
      scheduledend: addMinutes(start, 30).getTime(),
      offlineActivityId: 'offline_store_check_' + start.getTime(),
      subject: this.translate.instant(isPromoCheck ? 'STORE_PROMO_CHECK' : 'STORE_CHECK'),
    };
    if (context?.account) {
      payload.accounts = [{
        indskr_accountid: context.account.id,
        accountName: context.account.accountName,
        accountId: context.account.id,
      }];
      payload.subject += ` - ${context.account.accountName}`;
    }
    // A default category is optional — without one the check simply opens uncategorised.
    const defaultCategory = this.storeCheckService.productCategories?.find(pc => pc.indskr_default);
    if (defaultCategory) {
      payload.categoryId = defaultCategory.indskr_productfamilycategoryid;
      payload.categoryName = defaultCategory.indskr_name;
      if (isPromoCheck) payload.subject += ` - ${defaultCategory.indskr_name}`;
    }

    const check = await this.storeCheckService.createStoreCheck(payload);
    if (!check) return this.fail('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_STORE_CHECK');

    this.activityService.selectedActivity = check;
    this.activityService.selectedActivityAtHome = check;
    this.activityService.publishActivityEvent({ action: 'Create', activity: check });
    this.activityService.finalizeAgendaRefresh();
    this.uiService.showNewActivity = false;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.StoreCheckDetailsComponent,
          PageName.StoreCheckDetailsComponent,
          context.host,
        );
      } catch (error) {
        console.error('ActivityCreationService: host store check pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      return { created: true, activity: check };
    }

    await this.enterActivitiesTool();
    // The root switch clears selectedActivity, which the detail pane reads.
    this.activityService.selectedActivity = check;
    // The source path leaves these alone because it is already on the agenda; arriving from
    // another tool the right pane is hidden until activeView names it.
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.StoreCheckDetailsComponent,
      PageName.StoreCheckDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
    );
    this.uiService.showRightPane = true;
    return { created: true, activity: check };
  }

  /**
   * The agenda branch of NewActivityComponent.handleNewOrderCreation. The country and
   * opportunity fields stay layover-only; from an account card the customer is known, which
   * is the one field an agenda order is created bare without.
   */
  private async createSalesOrder(context?: QuickCreateContext): Promise<QuickCreateResult> {
    const currentTime = new Date().getTime();
    const start = Utility.getRoundedDownTime(new Date());
    const payload: any = {
      salesorderid: 'offline_order_' + currentTime,
      indskr_externalid: 'offline_order_' + currentTime,
      indskr_ordertype: 548910000,
      ordernumber: 'New Order',
      subject: 'New Order',
      name: 'New Order',
      pendingPushToDynamics: true,
      statuscode: 1,
      statecode: 0,
      activitytypecode: 'indskr_order',
      createdon: currentTime,
      scheduledstart: start.getTime(),
      scheduledend: addMinutes(start, 30).getTime(),
    };
    if (context?.account) {
      payload.customerid = context.account.id;
      payload.customerName = context.account.accountName;
    }
    const order = new OrderActivity(payload);
    // The class defaults ownerId to '' without these, which Dynamics rejects on a lookup.
    order.statusString = 'Open';
    order.ownerId = this.authService.user.systemUserID;
    order.ownerNameString = this.authService.user.displayName;

    try {
      await this.orderActivityDataService.createOrderActivity(
        { onDynamics: !this.device.isOffline, onLocalCopy: true, onLocalDatabase: true },
        [order], currentTime, false);
    } catch (error) {
      console.error('ActivityCreationService: createOrderActivity failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }

    this.trackingService.tracking('OrderCreate', TrackingEventNames.ORDER);
    this.trackingService.tracking(
      context?.host ? 'AccountNewOrder' : 'AgendaNewOrder', TrackingEventNames.ORDERMANAGEMENT);
    this.activityService.selectedActivity = order;
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: order.ID, subject: order.subject,
        location: order.accountId ? order.accountNameString : 'No Account',
        scheduledStart: order.scheduledStart, scheduledEnd: order.scheduledEnd, type: order.type,
      },
    });
    this.uiService.showNewActivity = false;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        // 'AccountTool' rather than the host's PageName: OrderDetailsComponent matches on
        // the string, and an unknown one leaves the pane with no way back.
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.OrderDetailsComponent,
          PageName.OrderDetailsComponent,
          context.host,
          { from: 'AccountTool', activity: order },
        );
      } catch (error) {
        console.error('ActivityCreationService: host order pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      this.activityService.finalizeAgendaRefresh();
      return { created: true, activity: order };
    }

    await this.enterActivitiesTool();
    // The root switch clears selectedActivity, which the detail pane reads.
    this.activityService.selectedActivity = order;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.OrderDetailsComponent,
      PageName.OrderDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: 'AgendaView', activity: order },
    );
    this.activityService.finalizeAgendaRefresh();
    if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
    this.uiService.showRightPane = true;
    return { created: true, activity: order };
  }

  /**
   * The agenda branch of NewActivityComponent.createNewAccountVisit. An AppointmentActivity
   * like the meeting, but a different endpoint and no meeting tail — indskr_isparentcall and
   * indskr_meetingtype are what make it a visit rather than a meeting. Online only.
   */
  private async createAccountVisit(context?: QuickCreateContext): Promise<QuickCreateResult> {
    const start = new Date();
    // Both gates toast their own reason, so neither may be toasted again here.
    if (!this.activityService.validateMeetingDate(start.toISOString())) return REFUSED();
    if (this.activityService.accountVisitOfflineCheck(
      { isAccountVisitEnabled: true, isAccountVisitRecord: true, isAccountVisitNestedMeeting: false }, true)) {
      return REFUSED();
    }

    const duration = this.authService.user?.meeting_duration || 30;
    const offlineMeetingId = this.activityService.generateOfflineMeetingId();
    const visit = new AppointmentActivity({
      subject: this.translate.instant('ACCOUNT_VISIT'),
      scheduledstart: start.getTime(),
      scheduledend: addMinutes(start, duration).getTime(),
      offlineMeetingId,
      indskr_positionid: this.authService.user.xPositionID,
      statecode: 0,
      statuscode: 1,
      activityid: offlineMeetingId,
      // Dropping indskr_ownerid here reproduces the procedure log's empty-owner rejection.
      indskr_ownerid: this.authService.user.systemUserID,
      indskr_ownerfullname: this.authService.user.displayName,
      indskr_isparentcall: true,
      indskr_meetingtype: 600000000,
    });

    // On the visit itself rather than through attachAccountsToMeeting: the record does not
    // exist yet, and the visit endpoint takes its accounts in the create payload.
    if (context?.account) {
      visit['accounts'] = [context.account];
      if (this.authService.user.buConfigs?.['indskr_populateaccountaddress']) {
        const address = await this.accountService.getPrimaryAddressOfAccount(context.account.id);
        await this.accountService.addLocationToMeeting(visit, address);
      }
    }

    const activityTypes = this.activityService.configuredActivityTypes(
      MeetingActivityTypeCode.MEETING, FormatType.ACCOUNT_VISIT) || [];
    const format = activityTypes.length === 1 ? activityTypes[0] : activityTypes.find(at => at.indskr_default);
    if (format) {
      const formatName = this.localizationService.getTranslatedString(format.indskr_name, EntityNameForTranslation.ActivityType);
      visit.indskr_activitytype = format.indskr_activitytypeid;
      visit.activityTypeName = formatName;
      visit.subject = `${this.translate.instant('ACCOUNT_VISIT')} - ${formatName}`;
    }

    let created = false;
    try {
      created = await this.meetingService.crudMeetingsOnlineWithOfflineUploadEndpoint([visit]);
    } catch (error) {
      console.error('ActivityCreationService: crudMeetingsOnlineWithOfflineUploadEndpoint failed', error);
    }
    if (!created) return this.fail('ACCOUNT_VISIT_ERROR_CREATING_NEW_ACCOUNT_VISIT', ToastStyle.INFO);

    this.activityService.selectedActivity = visit;
    this.activityService.selectedActivityAtHome = visit;
    if (!this.uiService.toolsActivityActive) {
      this.events.publish('refreshAgenda');
    } else {
      this.uiService.agendaRefreshRequired = true;
      this.activityService.signalActivityConflictCheck$.next(visit);
    }
    if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
    this.meetingStructureService.createEmbeddedInteractions(visit);
    this.activityDataService.activityDetailsLoaded = true;
    this.uiService.showNewActivity = false;

    if (context?.host) return this.goToDetailPaneInHost(visit, context.host);
    return this.goToDetailPaneInActivitiesTool(visit);
  }

  /**
   * The agenda branch of NewActivityComponent.handleNewProcedureTrackerCreation. A sibling of
   * createProcedureLog rather than a branch inside it: different entity, different data
   * service, and the only one of the three variants blocked offline.
   */
  private async createBulkProcedureLog(context?: QuickCreateContext): Promise<QuickCreateResult> {
    if (this.device.isOffline) {
      return this.fail(OFFLINE_REFUSALS.bulkProcedureLog, ToastStyle.INFO);
    }

    const currentTime = new Date().getTime();
    // Toasts its own reason past the grace period, so this must not toast again.
    if (!this.activityService.validateProcedureDate(new Date().toISOString(), 'DATETIME_P_CANT_SHEDULE_PROCEDURE_LOG_DAYS_AGO')) {
      return REFUSED();
    }

    const isWeekly = this.authService.user.procedureLogDuration === 'Weekly';
    const start = Utility.getRoundedDownTime(new Date());
    const startOfWeek = this.dateTimeFormatsService.getStartOfWeek(start);
    const endOfWeek = this.dateTimeFormatsService.getEndOfWeek(start);
    if (isWeekly) {
      this.notificationService.notify(this.translate.instant('PROCEDURE_LOG_FOR_WEEK', {
        startDate: this.dateTimeFormatsService.formatDate(startOfWeek, 'MMM DD, YYYY'),
        endDate: this.dateTimeFormatsService.formatDate(endOfWeek, 'MMM DD, YYYY'),
      }), 'New Activity', 'top', ToastStyle.INFO);
    }

    const tracker = new ProcedureTrackerActivity({
      salesorderid: 'offline_proceduretracker_' + currentTime,
      indskr_externalid: 'offline_proceduretracker_' + currentTime,
      indskr_ordertype: 548910002,
      ordernumber: 'Surgery Order',
      name: this.translate.instant('BULK_PROCEDURE_LOG'),
      pendingPushToDynamics: true,
      statuscode: 1,
      statecode: 0,
      activitytypecode: 'ProcedureTracker',
      createdon: currentTime,
      modifiedon: currentTime + '',
      scheduledstart: isWeekly ? startOfWeek.getTime() : start.getTime(),
      scheduledend: isWeekly ? endOfWeek.getTime() : addMinutes(start, 30).getTime(),
      divisionName: this.authService.user.businessUnitName,
      divisionId: this.authService.user.xBusinessUnitId,
      ...(context?.account ? {
        customerid: context.account.id,
        customerName: context.account.accountName,
      } : {}),
    });
    // The constructor derives its dates from indskr_scheduleddate, which the payload does
    // not carry, so a weekly end has to be put back on afterwards.
    if (isWeekly) tracker.scheduledEnd = endOfWeek;
    tracker.statusString = 'Draft';
    tracker.modifiedby = tracker.ownerId = this.authService.user.systemUserID;
    tracker.modifiedByName = tracker.ownerNameString = this.authService.user.displayName;

    try {
      await this.procedureTrackerActivityDataService.createOrderActivity(
        { onDynamics: true, onLocalCopy: true, onLocalDatabase: true },
        [tracker], currentTime, false);
    } catch (error) {
      console.error('ActivityCreationService: createOrderActivity (tracker) failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }

    this.trackingService.tracking('AgendanewProcedureTracker', TrackingEventNames.PROCEDURE_TRACKER);
    this.activityService.selectedActivity = tracker;
    this.activityService.selectedActivityAtHome = tracker;
    this.activityService.publishActivityEvent({ action: 'Create', activity: tracker });
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: tracker.ID, subject: tracker.subject,
        location: tracker.accountId ? tracker.accountNameString : 'No Account',
        scheduledStart: tracker.scheduledStart, scheduledEnd: tracker.scheduledEnd, type: tracker.type,
      },
    });
    this.uiService.showNewActivity = false;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.ProcedureTrackerDetailsComponent,
          PageName.ProcedureTrackerDetailsComponent,
          context.host,
          {
            from: context.host === PageName.AccountPageComponent
              ? 'ProcedureLogFromAccountTool'
              : 'ProcedureLogFromContactTool',
            activity: tracker,
          },
        );
      } catch (error) {
        console.error('ActivityCreationService: host procedure tracker pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      this.activityService.finalizeAgendaRefresh();
      return { created: true, activity: tracker };
    }

    await this.enterActivitiesTool();
    // The root switch clears selectedActivity, which the detail pane reads.
    this.activityService.selectedActivity = tracker;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.ProcedureTrackerDetailsComponent,
      PageName.ProcedureTrackerDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: 'AgendaView', activity: tracker },
    );
    this.activityService.finalizeAgendaRefresh();
    this.uiService.showRightPane = true;
    return { created: true, activity: tracker };
  }

  /**
   * The agenda branch of NewActivityComponent.handleNewSurgeryOrderCreation. Scoped to the
   * Procedure Log variants; Bulk Procedure Log is a separate entity on its own data service
   * and the only offline-blocked one, so it is not offered here.
   */
  private async createProcedureLog(category: ProcedureLogCategory | undefined, context?: QuickCreateContext): Promise<QuickCreateResult> {
    if (!category) return this.fail('SELECT_CATEGORY', ToastStyle.INFO);

    const currentTime = new Date().getTime();
    // Toasts its own reason past the grace period, so this must not toast again.
    if (!this.activityService.validateProcedureDate(new Date().toISOString(), 'DATETIME_P_CANT_SHEDULE_PROCEDURE_LOG_DAYS_AGO')) {
      return REFUSED();
    }

    const isWeekly = this.authService.user.procedureLogDuration === 'Weekly';
    const start = Utility.getRoundedDownTime(new Date());
    const end = addMinutes(start, 30);
    if (isWeekly) {
      this.notificationService.notify(this.translate.instant('PROCEDURE_LOG_FOR_WEEK', {
        startDate: this.dateTimeFormatsService.formatDate(this.dateTimeFormatsService.getStartOfWeek(start), 'MMM DD, YYYY'),
        endDate: this.dateTimeFormatsService.formatDate(this.dateTimeFormatsService.getEndOfWeek(start), 'MMM DD, YYYY'),
      }), 'New Activity', 'top', ToastStyle.INFO);
    }

    let payload: any = {
      salesorderid: 'offline_surgeryorder_' + currentTime,
      indskr_externalid: 'offline_surgeryorder_' + currentTime,
      indskr_ordertype: 548910001,
      ordernumber: 'Surgery Order',
      name: this.translate.instant('NEW_ACTIVITY_PROCEDURE_LOG'),
      indskr_noprocedureday: category === 'noProcedureDay',
      indskr_ctmergednew: false,
      pendingPushToDynamics: true,
      statuscode: 1,
      statecode: 0,
      activitytypecode: 'SurgeryOrder',
      createdon: currentTime,
      modifiedByName: this.authService.user.displayName,
      modifiedon: currentTime + '',
      scheduledstart: isWeekly ? this.dateTimeFormatsService.getStartOfWeek(start).getTime() : start.getTime(),
      scheduledend: isWeekly ? this.dateTimeFormatsService.getEndOfWeek(start).getTime() : end.getTime(),
    };
    if (this.authService.user.buSettings?.['indskr_setownerascoowner']) {
      payload = {
        ...payload,
        coOwners: [{
          userId: this.authService.user.xSystemUserID,
          userFullName: this.authService.user.displayName,
          indskr_procedurelogcoownerid: Guid.create().toString(),
        }],
      };
    }

    // From an account the account is the customer, for either category: New Activity's
    // _bindContextToOrderPayload sets it without looking at one.
    if (context?.account) {
      payload.customerid = context.account.id;
      payload.customerName = context.account.accountName;
    }

    // A procedure day is logged against the customer it was performed on. No Procedure Day
    // records the absence of one, so it carries no customer.
    if (context?.contact && category === 'procedureDay') {
      const contact = context.contact;
      payload.contactorders = [{
        id: contact.ID,
        firstName: contact.firstName,
        title: contact.title,
        middleName: contact.middleName,
        lastName: contact.lastName,
        statusCode: contact.statusCode,
      }];
      payload.name = `${this.translate.instant('NEW_ACTIVITY_PROCEDURE_LOG')} - ${contact.fullName}`;
      const primaryAccount = await this.accountService.findPrimaryAccountOfContact(contact.ID);
      if (primaryAccount) {
        payload.customerid = primaryAccount.accountid;
        payload.customerName = primaryAccount['indskr_accountcontactaffiliation.indskr_accountid_Formatted'];
      }
    }

    const order = new SurgeryOrderActivity(payload);
    // The constructor derives its dates from indskr_scheduleddate, which the payload does
    // not carry, so a weekly end has to be put back on afterwards.
    if (isWeekly) order.scheduledEnd = this.dateTimeFormatsService.getEndOfWeek(start);
    // The constructor defaults ownerId to '' when the payload carries no ownerid, and
    // serviceDTO sends it straight through — Dynamics rejects an empty string on a lookup.
    order.statusString = 'Draft';
    order.ownerId = this.authService.user.systemUserID;
    order.ownerNameString = this.authService.user.displayName;

    try {
      await this.surgeryOrderActivityDataService.createOrderActivity(
        { onDynamics: !this.device.isOffline, onLocalCopy: true, onLocalDatabase: true },
        [order], currentTime, false);
    } catch (error) {
      console.error('ActivityCreationService: createOrderActivity failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }

    this.trackingService.tracking('ProcedureLogCreate', TrackingEventNames.PROCEDURE_LOG);
    this.trackingService.tracking('AgendaNewSurgeryOrder', TrackingEventNames.SURGERY_ORDER);
    this.activityService.selectedActivity = order;
    this.activityService.selectedActivityAtHome = order;
    this.activityService.publishActivityEvent({ action: 'Create', activity: order });
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: order.ID, subject: order.subject,
        location: order.accountId ? order.accountNameString : 'No Account',
        scheduledStart: order.scheduledStart, scheduledEnd: order.scheduledEnd, type: order.type,
      },
    });
    this.uiService.showNewActivity = false;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.SurgeryOrderDetailsComponent,
          PageName.SurgeryOrderDetailsComponent,
          context.host,
          // SurgeryOrderDetailsComponent keys both its back button and its pop off these
          // tokens, not off a PageName — a host name leaves the pane with no way out.
          {
            from: context.host === PageName.AccountPageComponent
              ? 'ProcedureLogFromAccountTool'
              : 'ProcedureLogFromContactTool',
            activity: order,
          },
        );
      } catch (error) {
        console.error('ActivityCreationService: host procedure log pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      this.activityService.finalizeAgendaRefresh();
      return { created: true, activity: order };
    }

    await this.enterActivitiesTool();
    // The root switch clears selectedActivity, and the pane inits its own footer from it
    // in ngDoCheck — so this has to be back in place before the nav, not after.
    this.activityService.selectedActivity = order;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.SurgeryOrderDetailsComponent,
      PageName.SurgeryOrderDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppHomeComponent, activity: order },
    );
    this.activityService.finalizeAgendaRefresh();
    this.uiService.showRightPane = true;
    return { created: true, activity: order };
  }

  /**
   * The agenda branch of NewActivityComponent's 'customerinquiry' case. initNewCaseActivity
   * writes to disk only and already publishes the agenda refresh, so there is no HTTP call
   * to fail and no refresh to repeat here — the push to Dynamics happens on submit.
   */
  private async createCustomerInquiry(caseType: CaseType | undefined, context?: QuickCreateContext): Promise<QuickCreateResult> {
    // initNewCaseActivity dereferences the type unguarded, so a missing one is a refusal.
    if (!caseType) return this.fail('SELECT_CATEGORY', ToastStyle.INFO);

    // The contact branch pre-selects the customer and carries no start time; the agenda
    // branch has no customer to pre-select and stamps now.
    const preSelected: Record<string, unknown> = context?.contact
      ? { contacts: [context.contact] }
      : { startTime: new Date().getTime() };

    let inquiry: CaseActivity;
    try {
      inquiry = await this.caseManagementService.initNewCaseActivity(caseType, true, preSelected);
    } catch (error) {
      console.error('ActivityCreationService: initNewCaseActivity failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }
    if (!inquiry) return this.fail('ERROR_TRYAGAIN');

    this.trackingService.tracking('InquiryActivityConfirm', TrackingEventNames.CUSTOMER_INQUIRY);
    this.caseManagementService.assignSelectedCase(inquiry);
    this.caseManagementService.showNewCase = false;
    this.activityService.selectedActivity = inquiry;
    this.activityService.selectedActivityAtHome = inquiry;
    this.activityService.publishActivityEvent({ action: 'Create', activity: inquiry });
    this.uiService.showNewActivity = false;
    this.uiService.prevView = this.uiService.activeView;

    if (context?.host) {
      // Latched by CaseManagementDetailComponent's constructor, so it must precede the nav.
      // CONTACT rather than AGENDA: the pane closes back to the customer, not to the agenda.
      this.caseManagementService.accessedFrom = AccesingMode.CONTACT;
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.CaseManagementDetailComponent,
          PageName.CaseManagementDetailComponent,
          context.host,
          { from: PageName.ContactDetailsComponent },
        );
      } catch (error) {
        console.error('ActivityCreationService: host inquiry pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      return { created: true, activity: inquiry };
    }

    await this.enterActivitiesTool();
    // The root switch tears down the tool we came from, which clears selectedActivity.
    this.activityService.selectedActivity = inquiry;
    // Must precede the nav: CaseManagementDetailComponent's constructor latches isModalView
    // off this, and a stale in-meeting value would make the pane try to dismiss itself as a
    // modal. A stale PRESENTATION value hides the footer outright.
    this.caseManagementService.accessedFrom = AccesingMode.AGENDA;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    // The pane inits its own footer in ionViewDidEnter, so no initButtons call here.
    await this.navService.setChildNavRoot(
      PageName.CaseManagementDetailComponent,
      PageName.CaseManagementDetailComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppHomeComponent },
    );
    this.uiService.showRightPane = true;
    return { created: true, activity: inquiry };
  }

  /**
   * The agenda branch of NewActivityComponent.handleNewFollowUpActionTaskCreation. Category
   * 'none' is its default regardless of feature action, and it is the only one that makes
   * sense with no plan, opportunity or meeting to hang the task off. Midnight today with no
   * end, which is the date shape FollowUpActivity expects.
   */
  private async createFollowUpAction(context?: QuickCreateContext, category?: FOLLOW_UP_TYPE): Promise<QuickCreateResult> {
    const now = new Date().getTime();
    const payload: any = {
      activityId: 'offline_followup_' + now,
      activitytypecode: 'followuptask',
      subject: this.translate.instant('FOLLOW_UP_ACTION'),
      offlineId: 'offline_followup_' + now,
      scheduledstart: new Date().setHours(0, 0, 0, 0),
      indskr_plantype: category ?? FOLLOW_UP_TYPE.NONE,
    };
    // Only the category is set: the plan, opportunity, meeting or event it hangs off is the rep's to pick.
    if (context?.contact) {
      payload.indskr_contact = context.contact.ID;
      payload.contactName = context.contact.fullName;
      this.contactService.addToRecentContacts(context.contact);
    } else if (context?.account) {
      payload.indskr_account = context.account.id;
      payload.accountName = context.account.accountName;
    }
    const followUp = new FollowUpActivity(payload);
    followUp.state = 0;
    followUp.statusString = 'Open';
    followUp.ownerId = this.authService.user.systemUserID;
    followUp.ownerNameString = this.authService.user.displayName;
    followUp.pendingPushToDynamics = true;

    try {
      await this.followUpActivityDataService.createFollowUpActivity(
        { onDynamics: !this.device.isOffline, onLocalCopy: true, onLocalDatabase: true },
        [followUp], now, false);
    } catch (error) {
      console.error('ActivityCreationService: createFollowUpActivity failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }

    this.trackingService.tracking('AgendaFollowupAction', TrackingEventNames.ACCOUNTPLANNING);
    this.activityService.selectedActivity = followUp;
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: followUp.ID, subject: followUp.subject, location: followUp.location,
        scheduledStart: followUp.scheduledStart, scheduledEnd: followUp.scheduledEnd, type: followUp.type,
      },
    });
    this.activityService.publishActivityEvent({ action: 'Create', activity: followUp });
    this.uiService.showNewActivity = false;

    // Only the scientific task page has the plan field; its close handler keys off 'ContactTools'.
    const isScientificPlan = category === FOLLOW_UP_TYPE.SCIENTIFIC_PLAN;
    const detailPage = isScientificPlan ? PageName.ScientificPlanFollowUpTask : PageName.FollowUpActionDetailComponent;
    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          detailPage,
          detailPage,
          context.host,
          // FollowUpActionDetailComponent pops on these tokens; an unrecognised one leaves
          // its back button inert.
          {
            from: isScientificPlan ? 'ContactTools'
              : context.host === PageName.AccountPageComponent ? 'AccountTool' : 'ContactTool',
            activity: followUp,
          },
        );
      } catch (error) {
        console.error('ActivityCreationService: host follow up pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      this.activityService.finalizeAgendaRefresh();
      return { created: true, activity: followUp };
    }

    await this.enterActivitiesTool();
    // The root switch tears down the tool we came from, which clears selectedActivity.
    this.activityService.selectedActivity = followUp;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    // The pane prefers this param over selectedActivity, and inits its own footer — unlike
    // the phone call and allocation panes, so there is no initButtons call here.
    await this.navService.setChildNavRoot(
      detailPage,
      detailPage,
      PageName.GoAppActivitiesPageComponent,
      { from: 'AgendaView', activity: followUp },
    );
    this.activityService.finalizeAgendaRefresh();
    this.uiService.showRightPane = true;
    return { created: true, activity: followUp };
  }

  private async createTimeOff(): Promise<QuickCreateResult> {
    const tot = this.timeOffDataService.buildDefaultTimeOff();
    await this.timeOffDataService.createAndSaveTimeOff(tot);

    if (this.agendaFooterService.actSegment === 'week') {
      this.trackingService.tracking('TimeOffCalendarNew', TrackingEventNames.TIMEOFF, true);
    } else {
      this.trackingService.tracking('AgendaTimeOffCreated', TrackingEventNames.ACTIVITY);
    }
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: tot.timeOffRequestId, subject: tot.subject, location: tot.location,
        scheduledStart: new Date(tot.totStartTime).valueOf(), scheduledEnd: new Date(tot.totEndTime).valueOf(),
        type: ActivityType.TimeOffRequest, reason: tot.reason, allDay: tot.totIsAlldayEvent,
      },
    });
    this.timeOffService.popupSelectedEvent = this.timeOffUtilityService.decodeEventType(tot);
    this.timeOffService.totMode = 'myRequests';
    // Left by the Time Off tool; on a phone it routes back to that tool's handler, which never closes this pane.
    if (this.timeOffService.getMobileTracker() === 'timeOff') this.timeOffService.unsetMobileTracker();
    this.timeOffService.setSelectedTot(tot);
    this.activityService.selectedActivity = tot;
    this.uiService.showNewActivity = false;
    this.activityService.publishActivityEvent({ action: 'Create', activity: tot });

    await this.enterActivitiesTool();
    this.activityService.selectedActivity = tot;
    this.uiService.activeView = 'TimeOffDetail';
    await this.navService.setChildNavRoot(
      PageName.TimeOffDetailsComponent,
      PageName.TimeOffDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppHomeComponent, isNewlyCreatedActivity: true },
    );
    this.footerService.initButtons(FooterViews.TimeOffDetails);
    this.uiService.showRightPane = true;
    return { created: true, activity: tot };
  }

  /**
   * The agenda branch of NewActivityComponent.createNewPhoneCall. No format gate — the data
   * service resolves the default activity type itself and never refuses — and no offline
   * refusal, because createPhoneCallActivity writes an offline doc when there is no network.
   */
  private async createPhoneCall(context?: QuickCreateContext): Promise<QuickCreateResult> {
    // Rounded down to the half hour, which is what both customer surfaces have always done.
    // Unlike the meeting there is no BU setting to choose, so the shipped behaviour wins and
    // the top bar — which did not round at all — adopts it.
    const start = Utility.getRoundedDownTime(new Date());
    const duration = this.authService.user?.meeting_duration || 30;
    const label = this.translate.instant('NEW_ACTIVITY_PHONECALL');
    const payload = new InitiatePhoneCallPayload(
      start.getTime(),
      addMinutes(start, duration).getTime(),
      context?.contact?.fullName ? `${context.contact.fullName} - ${label}` : label,
      OFFLINE_PHONE_CALL_ID_PREFIX + new Date().getTime(),
      0,
      undefined,
    );

    // Adds the record to the agenda itself, unlike the meeting and message paths.
    const phoneCall = await this.phoneCallDataService.createPhoneCallActivity(payload);
    if (!phoneCall) return this.fail('ERROR_TRYAGAIN');

    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: phoneCall.createdOffline
        ? { id: phoneCall.ID, subject: phoneCall.subject, location: phoneCall.location, scheduledStart: phoneCall.scheduledStart, scheduledEnd: phoneCall.scheduledEnd }
        : { id: phoneCall.ID },
    });

    this.activityService.selectedActivity = phoneCall;
    if (context?.contact) await this.attachContactToPhoneCall(phoneCall, context.contact);
    else if (context?.account) await this.attachAccountToPhoneCall(phoneCall, context.account);
    this.activityService.addIoConfigDefaultValuesToActivity(phoneCall);
    if (phoneCall.appConfigFields?.length > 0) {
      if (!this.device.isOffline && !this.activityService.hasOfflinePhoneCallData(phoneCall.ID)) {
        await this.phoneCallDataService.updateappConfigFields(phoneCall, phoneCall.appConfigFields, true);
      } else {
        await this.activityService.upsertPhoneCallOfflineData(phoneCall);
      }
    }

    this.activityDataService.activityDetailsLoaded = true;
    this.uiService.showNewActivity = false;
    if (!this.uiService.toolsActivityActive) {
      this.events.publish('refreshAgenda');
      if (this.agendaFooterService.actSegment === 'week') this.events.publish('weekview:RefreshUI');
    } else {
      this.uiService.agendaRefreshRequired = true;
      this.activityService.signalActivityConflictCheck$.next(phoneCall);
    }
    this.phoneCallMeetingStructureService.createEmbeddedInteractions(phoneCall);
    this.phoneCallDataService.saveMeeting(phoneCall);

    if (context?.host) {
      return this.goToPhoneCallDetailPaneInHost(phoneCall, context.host, context.restoreView);
    }

    await this.enterActivitiesTool();
    // The root switch tears down the tool we came from, which clears selectedActivity.
    this.activityService.selectedActivity = phoneCall;
    this.uiService.activeView = 'PhoneCall';
    await this.navService.setChildNavRoot(
      PageName.PhoneCallDetailsComponent,
      PageName.PhoneCallDetailsComponent,
      PageName.GoAppActivitiesPageComponent,
      { isNewlyCreatedActivity: true },
    );
    this.footerService.initButtons(FooterViews.PHONE_CALL);
    this.uiService.showRightPane = true;
    return { created: true, activity: phoneCall };
  }

  /**
   * The agenda branch of NewActivityComponent.createNewSampleRequest. The rep picks the
   * contact and the products afterwards in the detail pane, which is why the footer opens
   * with eligibilities and signature disabled.
   */
  private async createAllocationOrder(context?: QuickCreateContext): Promise<QuickCreateResult> {
    const start = new Date();
    const contact = context?.contact;
    const address = contact ? await this.singleLicensedAddress(contact) : null;
    if (contact) this.contactService.addToRecentContacts(contact);

    const payload = new CreateSampleDropRequestBody({
      scheduledStart: start,
      scheduledEnd: addMinutes(start, 30),
      offlineActivityId: 'offlineSampleOrder_' + start.getTime(),
      subject: contact
        ? `${this.translate.instant('ALLOWCATION_ORDER')} ${this.translate.instant('WITH')} ${contact.fullName}`
        : this.translate.instant('ALLOWCATION_ORDER'),
      contactID: contact?.ID ?? '',
      addressID: address?.customerAddressID ?? '',
    });
    payload.indskr_ownerid = this.authService.user.systemUserID;

    let order: SampleActivity;
    try {
      order = await this.sampleDataService.initiateSampleOrder(payload);
    } catch (error) {
      console.error('ActivityCreationService: initiateSampleOrder failed', error);
      return this.fail('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_ALLOWCATION_ORDER', ToastStyle.INFO);
    }
    if (!order) return this.fail('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_ALLOWCATION_ORDER', ToastStyle.INFO);
    if (contact && order.contactID) order.contactName = contact.fullName;

    this.trackingService.tracking('AgendaAllocationCreated', TrackingEventNames.ACTIVITY);
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: order['activityId']
        ? { id: order['activityId'], ...payload }
        : { id: order.offlineActivityId, subject: order.subject, location: order.location, scheduledStart: order.scheduledStart, scheduledEnd: order.scheduledEnd },
    });

    this.activityService.selectedActivity = order;
    await this.activityService.addActivity(order);
    if (!this.uiService.toolsActivityActive) {
      this.events.publish('refreshAgenda');
    } else {
      this.uiService.agendaRefreshRequired = true;
    }
    this.activityService.publishActivityEvent({ action: 'Create', activity: order });
    this.activityDataService.activityDetailsLoaded = true;
    this.uiService.showNewActivity = false;

    // Read before the nav either way: NewSampleActivityComponent decides the in-meeting flow
    // from it, and only clears it on destroy when activeView is not 'Sample' — so a stale
    // CREATE_FROM_MEETING would otherwise survive into this one.
    this.sampleService.samplingDetailsViewMode = SamplingDetailsViewMode.CREATE_FROM_AGENDA;

    if (context?.host) {
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.NewSampleActivityComponent,
          PageName.NewSampleActivityComponent,
          context.host,
          { from: context.host },
        );
      } catch (error) {
        console.error('ActivityCreationService: host allocation pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      this.footerService.initButtons(FooterViews.SampleDropDetails);
      this.footerService.disableButton(['samplingeligibilities', 'signature']);
      return { created: true, activity: order };
    }

    await this.enterActivitiesTool();
    // The root switch tears down the tool we came from, which clears selectedActivity.
    this.activityService.selectedActivity = order;
    this.uiService.activeView = 'Sample';
    await this.navService.setChildNavRoot(
      PageName.NewSampleActivityComponent,
      PageName.NewSampleActivityComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppHomeComponent },
    );
    this.footerService.initButtons(FooterViews.SampleDropDetails);
    this.footerService.disableButton(['samplingeligibilities', 'signature']);
    this.uiService.showRightPane = true;
    return { created: true, activity: order };
  }

  /**
   * The address to pre-fill, or none. Only a contact with exactly one address gets one, and
   * only when the rep is licensed in that state — with several the rep picks, and an
   * unlicensed one must not be filled in for them.
   */
  private async singleLicensedAddress(contact: Contact): Promise<any | null> {
    const addresses = uniqBy(contact.addressesList ?? [], 'addressId');
    if (addresses.length !== 1) return null;
    return await this.sampleDataService.hasValidStateLicense(addresses[0].stateId, contact.ID)
      ? addresses[0] : null;
  }

  /**
   * The agenda branch of NewActivityComponent.createDraftEmail, minus every master-page
   * and layover case: scope 1 always lands in Activities, so only that branch can run.
   */
  private async createMessage(
    channel: EmailActivityChannelDetails | undefined,
    context?: QuickCreateContext,
    firstTimeEmail = false,
  ): Promise<QuickCreateResult> {
    if (!channel) return this.fail('SELECT_CHANNEL', ToastStyle.INFO);

    let emailParties: EmailActivityParty[] = [];
    if (context?.contact) {
      const refusal = await this.checkMessageGates(channel, context.contact, firstTimeEmail);
      if (refusal) return refusal;
      emailParties = this.buildEmailParties(context.contact, firstTimeEmail);
    }

    const now = new Date().getTime().toString();
    const payload: any = {
      scheduledstart: now,
      scheduledend: now,
      subject: channel.channelType,
      statuscode: 1,
      channelType: channel.channelType,
      channelId: channel.id,
      channelActivityType: channel.activityType,
    };
    if (firstTimeEmail) payload.indskr_type = EmailTemplateType.FirstTimeEmail;

    const activity = await this.emailService.createNewEmailActivity(payload, false);
    if (!activity) return this.fail('ERROR_TRYAGAIN');

    this.trackingService.tracking('AgendaEmailCreated', TrackingEventNames.ACTIVITY);
    await this.activityService.addActivity(activity);
    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: {
        id: activity.ID, subject: activity.subject, info: activity.info, type: activity.type,
        scheduledStart: activity.scheduledStart, scheduledEnd: activity.scheduledEnd,
      },
    });

    this.emailService.selectedActivity = activity;
    this.emailService.setCurrentEmail(activity);
    this.emailService.viewType = EmailViewType.FROM_AGENDA;
    // OMNI-59747: the email footer reuses generic activity logic that reads this one.
    this.activityService.selectedActivity = activity;
    this.uiService.showNewActivity = false;
    this.activityDataService.activityDetailsLoaded = true;
    this.activityService.finalizeAgendaRefresh();
    this.activityService.publishActivityEvent({ action: 'Create', activity });

    // After the record exists: the parties carry the contact's addresses and the
    // do-not-email flag, which the send path reads back off the activity.
    if (emailParties.length) await this.emailService.updateEmailActivityParties(emailParties);

    if (context?.host) {
      this.uiService.showNewActivity = false;
      this.uiService.activeView = 'Email';
      this.uiService.showRightPane = true;
      this.navService.setChildNavRightPaneView(true);
      try {
        await this.navService.pushChildNavPageWithPageTracking(
          PageName.EmailDetailsPageComponent,
          PageName.EmailDetailsPageComponent,
          context.host,
          { from: context.host },
        );
      } catch (error) {
        console.error('ActivityCreationService: host message pane push failed', error);
        return this.fail('ERROR_TRYAGAIN');
      }
      return { created: true, activity };
    }

    await this.enterActivitiesTool();
    this.activityService.selectedActivity = activity;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.EmailDetailsPageComponent,
      PageName.EmailDetailsPageComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppActivitiesPageComponent },
    );
    this.uiService.showRightPane = true;
    return { created: true, activity };
  }

  /**
   * Refusals a message to a specific contact can hit. A first-time-email request skips the
   * credential check on purpose — the whole point is that the rep has no address yet.
   */
  private async checkMessageGates(
    channel: EmailActivityChannelDetails, contact: Contact, firstTimeEmail: boolean,
  ): Promise<QuickCreateResult | null> {
    if (!contact.isActive) return this.fail('CONTACT_NO_LONGER_ACCESS');
    if (!firstTimeEmail) {
      const missing = MISSING_CHANNEL_CREDENTIAL[channel.activityType]?.(contact);
      if (missing) return this.fail(missing, ToastStyle.INFO);
      await this.emailService.fetchDoNotEmailForSelectedContacts([contact]);
      // OMNI-61541: AMA No-Contact. Read after the fetch, which refreshes the cached flag.
      if (contact.donotemail === true) return this.fail('AMA_NO_CONTACT_CANNOT_BE_MESSAGED', ToastStyle.INFO);
    }
    return null;
  }

  private buildEmailParties(contact: Contact, firstTimeEmail: boolean): EmailActivityParty[] {
    const party = new EmailActivityParty();
    party.allowToRemove = true;
    party.contact_firstname = contact.firstName;
    party.contact_lastname = contact.lastName;
    party.contact_mobilephone = contact.mobilePhone;
    party.contact_indskr_facebookpsid = contact.indskr_facebookpsid;
    party.indskr_contactid = contact.ID;
    party.emailAddresses = [];

    if (!firstTimeEmail) {
      const seen = new Set<string>();
      for (const email of contact.emailAddressList ?? []) {
        email.isSelected = !!email.isPrimary;
        if (email.isPrimary && !seen.has(email.emailAddressId)) {
          seen.add(email.emailAddressId);
          party.emailAddresses.push(new EmailAddress(email.emailAddressId, email.emailAddress));
        }
      }
      // Propagated unconditionally: the AMA send-block is independent of MESSAGE_CONSENT_REQUIRED.
      if (contact.donotemail !== undefined) party.donotemail = contact.donotemail;
    }
    return [party];
  }

  private async createMeeting(
    chosenFormat: MeetingActivityType | undefined,
    displacedActivity: any,
    context?: QuickCreateContext,
  ): Promise<QuickCreateResult> {
    const start = this.meetingStart();
    const activityTypes = this.meetingFormats();

    const refusal = this.checkMeetingGates(start, activityTypes, chosenFormat);
    if (refusal) return refusal;

    const payload = this.buildMeetingPayload(start, activityTypes, chosenFormat, context);
    const meeting = await this.meetingService.createNewMeeting(payload);

    // Sticky flag on the shared data service: every other caller clears it after reading,
    // and leaving it set would make every later create look like a failure.
    const startDateRejected = this.meetingService.isErrorStartDateCreateMeeting;
    this.meetingService.isErrorStartDateCreateMeeting = false;

    if (!meeting || startDateRejected) {
      this.meetingStructureService.createEmbeddedInteractions(null);
      // createNewMeeting already toasted the date reason on ERR_IO_ME12.
      return startDateRejected ? REFUSED() : this.fail('NEW_ACTIVITY_TOAST_ERROR_CREATING_NEW_MEETING');
    }

    this.trackingService.tracking(
      this.agendaFooterService.actSegment === 'week' ? 'CalendarNewActivity' : 'AgendaMeetingCreated',
      TrackingEventNames.ACTIVITY);

    // Before the tail: reconcileSubject runs inside it and recomputes from the attendee,
    // so attaching first is what makes "Dr Sharma - Meeting" come out right.
    if (context?.contact) await this.attachContactToMeeting(meeting, context.contact);
    else if (context?.account) {
      await this.attachAccountsToMeeting(meeting, [context.account]);
      const affiliated = await this.contactService.getAffiliatedContactsFromAccountsForMeeting([context.account]);
      if (affiliated.length === 1) await this.attachContactToMeeting(meeting, affiliated[0], false);
    }

    await this.runMeetingTail(meeting, payload);

    if (context?.host) return this.goToDetailPaneInHost(meeting, context.host);
    // From the top bar the meeting always opens in the Activities tool, whichever tool the
    // rep started from. Hosting it in that tool's right pane is parked, not dropped.
    // return this.goToDetailPane(meeting, displacedActivity);
    return this.goToDetailPaneInActivitiesTool(meeting);
  }

  /**
   * What New Activity's picker offers a customer surface, gated the same way
   * (getFollowUpActionCategories). The account tool drops the scientific plan; only the
   * meeting task is left out here, because it exists solely inside a meeting.
   *
   * None of these needs a parent record: the plan and opportunity fields are filled only
   * when the layover carried one, so a task created from a card is typed and otherwise bare,
   * exactly as New Activity creates it from the same place.
   */
  public followUpCategories(source: QuickActionSource = 'contact'): { label: string, value: FOLLOW_UP_TYPE }[] {
    const fa = (action: FeatureActionsMap) => this.authService.hasFeatureAction(action);
    const categories = [{ label: this.translate.instant('NONE'), value: FOLLOW_UP_TYPE.NONE }];
    if (fa(FeatureActionsMap.ACCOUNT_MANAGEMENT) && fa(FeatureActionsMap.ACCOUNT_FOLLOW_UP_TASK)) {
      categories.push({ label: this.translate.instant('NEW_ACTIVITY_ACCOUNT_PLAN_ACTION'), value: FOLLOW_UP_TYPE.ACCOUNT_PLAN });
    }
    if (source !== 'account' && fa(FeatureActionsMap.SCIENTIFIC_TOOL_PLAN) && fa(FeatureActionsMap.SCIENTIFIC_FOLLOW_UP_TASK)) {
      categories.push({ label: this.translate.instant('NEW_ACTIVITY_SCIENTIFIC_PALN_ACTION'), value: FOLLOW_UP_TYPE.SCIENTIFIC_PLAN });
    }
    if (fa(FeatureActionsMap.OPPORTUNITY_MANAGEMENT_TASK)) {
      categories.push({ label: this.translate.instant('NEW_ACTIVITY_OPPORTUNITY_ACTION'), value: FOLLOW_UP_TYPE.OPPORTUNITY_TASK });
    }
    if (fa(FeatureActionsMap.MARKETING_BUSINESS_PLAN)) {
      categories.push({ label: this.translate.instant('MARKETING_BUSINESS_PLAN_ACTION'), value: FOLLOW_UP_TYPE.MARKETING_BUSINESS_PLAN });
    }
    if (fa(FeatureActionsMap.EVENT_FOLLOW_UP_ACTION)) {
      categories.push({ label: this.translate.instant('EVENT_ACTION'), value: FOLLOW_UP_TYPE.EVENT_TASK });
    }
    return categories;
  }

  public meetingFormats(): MeetingActivityType[] {
    return this.activityService.configuredActivityTypes(MeetingActivityTypeCode.MEETING, FormatType.HCP_MEETING) || [];
  }

  public canDictateMeeting(): boolean {
    return this.authService.hasFeatureAction(FeatureActionsMap.VOICE_NOTE_TAKER);
  }

  // With the mic on, Meeting always opens its dropdown, so the mic has somewhere to be.
  public meetingAsksFirst(): boolean {
    return this.meetingFormats().length > 1 || this.canDictateMeeting();
  }

  public meetingFormatOptions(): IndActionMenuOption[] {
    const dictatable = this.canDictateMeeting();
    return this.meetingFormats().map(format => ({
      id: format.indskr_activitytypeid,
      label: this.localizationService.getTranslatedString(format.indskr_name, EntityNameForTranslation.ActivityType),
      dictatable,
    }));
  }

  // VoiceNoteMeetingService is otherwise loaded only lazily, so it is imported on demand here too.
  // Without a format id (the Meeting row's own mic), the format New Activity would preselect.
  public async dictateMeeting(formatId?: string, context?: QuickCreateContext): Promise<AppointmentActivity | null> {
    if (this._inFlight.value) return null;
    this._inFlight.next(true);
    const formats = this.meetingFormats();
    const format = formatId
      ? formats.find(candidate => candidate.indskr_activitytypeid === formatId)
      : this.defaultMeetingFormat(formats);
    try {
      const [{ DictationFlowService }, { VoiceNoteMeetingService }] = await Promise.all([
        import('../voice-note/dictation-flow.service'),
        import('../voice-note/voice-note-meeting.service'),
      ]);
      const dictation = await this.injector.get(DictationFlowService).dictateNewMeeting(
        format?.indskr_activitytypeid,
        format ? this.localizationService.getTranslatedString(format.indskr_name, EntityNameForTranslation.ActivityType) : undefined,
        knownCustomerFrom(context),
      );
      if (!dictation) return null;

      // Its own Activities landing targets the active child nav, so it needs the Activities page up.
      if (!context?.host) await this.enterActivitiesTool();
      const meeting = await this.injector.get(VoiceNoteMeetingService).createMeetingFromDictation(
        dictation.confirmed,
        format ? { activityTypeId: format.indskr_activitytypeid, activityTypeName: format.indskr_name } : undefined,
        { transcript: dictation.transcript, sourceNote: dictation.sourceNote, openIn: context?.host ? 'none' : 'activities' },
      );
      if (!meeting) {
        this.fail('SOMETHING_REALLY_BAD_HAPPENED');
        return null;
      }
      if (context?.host) {
        this.activityDataService.activityDetailsLoaded = true;
        await this.activityDataService._appendMeetingDetailsToActivity(meeting);
        await this.goToDetailPaneInHost(meeting, context.host);
      }
      return meeting;
    } catch (error) {
      console.error('ActivityCreationService: dictated meeting failed', error);
      this.fail('ERROR_TRYAGAIN');
      return null;
    } finally {
      this._inFlight.next(false);
    }
  }

  /** The refusal create() would toast for this type right now, or null while it can run. */
  public offlineRefusal(type: QuickActivityType): string | null {
    return this.device.isOffline ? OFFLINE_REFUSALS[type] ?? null : null;
  }

  /** The same list New Activity builds; No Procedure Day is BU-suppressible. */
  public procedureLogCategories(): { label: string, value: ProcedureLogOption }[] {
    const user = this.authService.user;
    if (!this.authService.hasFeatureAction(FeatureActionsMap.PROCEDURE_LOG)) return [];
    const categories: { label: string, value: ProcedureLogOption }[] = [];
    if (user.hasProcedureLog) {
      categories.push({ label: this.translate.instant('PROCEDURE_DAY'), value: 'procedureDay' });
      if (!user.buSettings?.['indskr_noprocedureday']) {
        categories.push({ label: this.translate.instant('NO_PROCEDURE_DAY'), value: 'noProcedureDay' });
      }
    }
    if (user.hasBulkProcedureLog) {
      categories.push({ label: this.translate.instant('BULK_PROCEDURE_LOG'), value: BULK_PROCEDURE_LOG });
    }
    return categories;
  }

  /** Bulk is a different entity on its own create; it reads only the host and account from context. */
  public createProcedureLogOf(option: ProcedureLogOption | undefined, context?: QuickCreateContext): Promise<QuickCreateResult> {
    return option === BULK_PROCEDURE_LOG
      ? this.create('bulkProcedureLog', { context })
      : this.create('procedureLog', { procedureLogCategory: option, context });
  }

  /**
   * Rounding is a BU setting, so it applies wherever a meeting is created — the top bar
   * honoured neither half of it before this was shared.
   */
  private meetingStart(): Date {
    return this.authService.user?.buSettings?.['indskr_meetingstarttimerounding'] === true
      ? Utility.getRoundedUpTime(new Date())
      : new Date();
  }

  private checkMeetingGates(start: Date, activityTypes: MeetingActivityType[], chosenFormat?: MeetingActivityType): QuickCreateResult | null {
    if (activityTypes.some(at => at.indskr_mandatory) && !chosenFormat) {
      return this.fail('SELECT_MEETING_FORMAT', ToastStyle.INFO);
    }
    // Toasts its own reason (past date / maxMeetingStartDays), so this must not toast again.
    if (!this.activityService.validateMeetingDate(start.toISOString())) {
      return REFUSED();
    }
    return null;
  }

  private buildMeetingPayload(
    start: Date,
    activityTypes: MeetingActivityType[],
    chosenFormat?: MeetingActivityType,
    context?: QuickCreateContext,
  ): InitiateMeetingPayload {
    const duration = this.authService.user?.meeting_duration || 30;
    const format = chosenFormat || this.defaultMeetingFormat(activityTypes);
    const formatName = format
      ? this.localizationService.getTranslatedString(format.indskr_name, EntityNameForTranslation.ActivityType)
      : '';
    const subType = this.defaultMeetingSubFormat(format);

    const payload = new InitiateMeetingPayload(
      this.meetingSubject(context?.contact?.fullName),
      this.translate.instant('NO_LOCATION'),
      start.getTime(),
      addMinutes(start, duration).getTime(),
      '',
      this.activityService.generateOfflineMeetingId(),
      undefined,
      null,
      '',
      format?.indskr_activitytypeid,
      formatName || null,
      subType?.indskr_activitysubtypeid,
      subType ? this.localizationService.getTranslatedString(subType.indskr_name, EntityNameForTranslation.ActivityType) : null,
    );

    if (this.secondaryInfoService.isAppointmentSecInfoConfigured) {
      payload.subject = this.meetingService.setMeetingSubject(new AppointmentActivity({
        subject: payload.subject,
        location: payload.location,
        scheduledstart: payload.scheduledStart,
        scheduledend: payload.scheduledEnd,
        activityid: payload.offlineMeetingId,
        indskr_ownerfullname: this.authService.user.displayName,
        indskr_activitytype: payload.indskr_activitytypeid,
        activityTypeName: payload.activityTypeName,
        indskr_activitysubtype: payload.indskr_activitysubtypeid,
        activitySubTypeName: payload.activitySubTypeName,
      }));
      payload.isSubjectFinalForCreate = true;
    }

    // The create endpoint embeds the attendee when it can, which saves a second round trip;
    // attachContactToMeeting falls back to adding it afterwards when it did not.
    if (context?.contact) payload.contacts = [context.contact];

    return payload;
  }

  /**
   * A customer's name leads the subject when there is one. Sec-info still overrides the
   * whole thing later in reconcileSubject; this is what the record is created with.
   */
  private meetingSubject(customerName?: string): string {
    if (this.authService.hasFeatureAction(FeatureActionsMap.VISIT_AUTO_SUBJECT)) {
      const visit = this.translate.instant('VISIT');
      return customerName ? `${visit} - ${customerName}` : visit;
    }
    const meeting = this.translate.instant('MEETING');
    if (!this.secondaryInfoService.isAppointmentSecInfoConfigured
      && this.authService.hasFeatureAction(FeatureActionsMap.MEETING_AUTO_SUBJECT)) {
      return customerName
        ? `${customerName} - ${meeting} - ${this.authService.user.displayName}`
        : `${meeting} - ${this.authService.user.displayName}`;
    }
    return customerName ? `${customerName} - ${meeting}` : meeting;
  }

  private defaultMeetingFormat(activityTypes: MeetingActivityType[]): MeetingActivityType {
    if (!activityTypes.length) return null;
    return activityTypes.find(at => at.indskr_default)
      || (activityTypes.some(at => at.indskr_mandatory) ? activityTypes[0] : null);
  }

  private defaultMeetingSubFormat(format: MeetingActivityType) {
    if (!format?.indskr_activitytypeid) return null;
    const subTypes = this.activityService.getActivitySubTypesByActivityType(
      MeetingActivityTypeCode.MEETING, format.indskr_activitytypeid) || [];
    return subTypes.find(st => st.indskr_default) || null;
  }

  private async runMeetingTail(meeting: any, payload: InitiateMeetingPayload): Promise<void> {
    this.diskService.updateOrInsertActivityToActivityDetailRawDocument(meeting);

    this.activityService.addIoConfigDefaultValuesToActivity(meeting);
    if (meeting.appConfigFields?.length > 0 && !this.device.isOffline && !this.activityService.hasOfflineMeetingData(meeting.ID)) {
      try {
        await this.meetingService.updateappConfigFields(meeting, meeting.appConfigFields, true);
      } catch (error) {
        console.error('ActivityCreationService: saveAdditionalConfigFields', error);
        this.activityService.upsertMeetingsOfflineData(meeting, false, true);
      }
    }

    this.activityService.selectedActivity = meeting;
    if (!meeting['isCreatePendingSync']) {
      await this.activityService.upsertMeetingsOfflineData(meeting);
    }
    this.activityService.publishActivityEvent({ action: 'Create', activity: meeting });

    this.uiService.setUIServiceData({
      view: 'new-activity',
      data: meeting.createdOffline
        ? { id: meeting.ID, subject: meeting.subject, location: meeting.location, scheduledStart: meeting.scheduledStart, scheduledEnd: meeting.scheduledEnd }
        : { id: meeting.ID, ...payload },
    });

    await this.reconcileSubject(meeting);

    if (!this.uiService.toolsActivityActive) {
      this.events.publish('refreshAgenda');
    } else {
      this.uiService.agendaRefreshRequired = true;
      this.activityService.signalActivityConflictCheck$.next(meeting);
    }
    if (this.agendaFooterService.actSegment === 'week') {
      this.events.publish('weekview:RefreshUI');
    }

    this.meetingStructureService.createEmbeddedInteractions(meeting);
    this.activityDataService.activityDetailsLoaded = true;
  }

  /**
   * Numbers a phone call may be placed on, in the order the rep is offered them. The first
   * one the contact actually has becomes the selection.
   */
  private async attachContactToPhoneCall(phoneCall: PhoneActivity, contact: Contact): Promise<void> {
    this.contactService.moveContactToSelected(contact);
    phoneCall.selectedMobileNumber = PHONE_NUMBER_FIELDS
      .map(field => contact[field])
      .find(number => !!number) ?? contact.mobilePhone;
    await this.phoneCallDataService.addContactsToPhoneCallActivity(phoneCall);
  }

  /**
   * The account card's equivalent. The payload cannot carry it - the record has to exist
   * before accounts can be attached - so it is patched, or held offline, right after.
   */
  private async attachAccountToPhoneCall(phoneCall: PhoneActivity, account: Account): Promise<void> {
    phoneCall['accounts'] = [account];
    const payload = this.phoneCallDataService.setPhoneCallPayload(phoneCall);
    if (!this.device.isOffline && !this.activityService.hasOfflinePhoneCallData(phoneCall.ID)) {
      await this.phoneCallDataService.updatePhoneCall(phoneCall, payload);
    } else {
      await this.activityService.upsertPhoneCallOfflineData(phoneCall);
    }
  }

  /**
   * Puts the contact on the meeting, and everything that follows from it: the location it
   * implies, and the accounts it is affiliated to unless the meeting already has its account.
   */
  private async attachContactToMeeting(meeting: AppointmentActivity, contact: Contact, withAffiliatedAccounts = true): Promise<void> {
    const embedded = (meeting._raw_contactAttendees as any[])?.some(r => r.indskr_contactid === contact.ID);
    if (!embedded) {
      meeting['contacts'].push(contact);
      const added = await this.meetingService.addContactsToMeeting(meeting, true);
      if (!added) await this.activityService.upsertMeetingsOfflineData(meeting);
    }

    this.contentMatchingService.isNewContactAddedToMeeting = true;
    this.activityService.selectedActivity = meeting;

    await this.applyContactLocation(meeting, contact);
    if (withAffiliatedAccounts) await this.applyAffiliatedAccounts(meeting, contact);
  }

  /**
   * The contact's own address becomes the location, unless GPS check-in owns it or the BU
   * has said the account's address wins — in which case applyAffiliatedAccounts sets it.
   */
  private async applyContactLocation(meeting: AppointmentActivity, contact: Contact): Promise<void> {
    if (!contact.addressesList?.length
      || this.authService.hasFeatureAction(FeatureActionsMap.ENABLE_GPS_CHECK_IN)
      || this.authService.user.buConfigs?.['indskr_populateaccountaddress']) {
      return;
    }

    await this.contactService.setLocationField(contact);

    if (this.device.isOffline) {
      await this.activityService.upsertMeetingsOfflineData(meeting);
      return;
    }
    const skipPatch = shouldSkipPostCreateMeetingPatch({
      isOffline: false,
      hasOfflineData: this.activityService.hasOfflineMeetingData(meeting.ID),
      isAppointmentSecInfoConfigured: true,
      createFields: meeting['createRequestFields'] || {},
      finalFields: {
        subject: meeting.subject,
        location: meeting.location,
        scheduledStart: meeting.scheduledStart,
        scheduledEnd: meeting.scheduledEnd,
      },
    });
    if (!skipPatch) {
      await this.meetingService.updateMeeting(meeting,
        new UpdateMeetingPayload(meeting.subject, meeting.location, meeting.scheduledStart, meeting.scheduledEnd));
    }
  }

  /**
   * OMNI-59164's rule: a contact affiliated to exactly one account puts that account on the
   * meeting; otherwise only its primary account does, and only if the contact is affiliated
   * to it. Several affiliations with no primary attach nothing — the rep picks.
   */
  private async applyAffiliatedAccounts(meeting: AppointmentActivity, contact: Contact): Promise<void> {
    if (this.authService.hasIoConfigWithFeatureCode(IoConfigurationFeatureCodes.BU_APPOINTMENT_GA_HIDE_ACCOUNT_FIELD)) return;
    const affiliated = await this.accountService.getAffiliatedAccountsFromSelectedContactsForMeeting([contact]);
    const accounts = affiliated.length === 1
      ? affiliated
      : contact.primaryAccount ? affiliated.filter(a => a.id === contact.primaryAccount.id) : [];
    if (!accounts.length) return;
    await this.attachAccountsToMeeting(meeting, accounts);
  }

  /** Shared by the contact path, which derives its accounts, and the account card, which is one. */
  private async attachAccountsToMeeting(meeting: AppointmentActivity, accounts: Account[]): Promise<void> {
    if (meeting.type !== ActivityType.Appointment || meeting.isCompleted) return;
    // moveAccountToSelected writes to whichever activity is selected, not to this meeting.
    this.activityService.selectedActivity = meeting;
    meeting['accounts'] = [];
    accounts.forEach(account => this.accountService.moveAccountToSelected(account));

    if (this.authService.user.buConfigs?.['indskr_populateaccountaddress'] && accounts.length) {
      try {
        const address = await this.accountService.getPrimaryAddressOfAccount(accounts[0].id);
        await this.accountService.addLocationToMeeting(meeting, address);
      } catch (error) {
        console.error('ActivityCreationService: account address lookup failed', error);
      }
    }

    if (this.device.isOffline || this.activityService.hasOfflineMeetingData(meeting.ID)) {
      await this.activityService.upsertMeetingsOfflineData(meeting);
      return;
    }
    try {
      await this.meetingService.addAccountsToMeeting(meeting);
    } catch (error) {
      console.error('ActivityCreationService: addAccountsToMeeting failed', error);
      await this.activityService.upsertMeetingsOfflineData(meeting, false, true);
    }

    // The sec-info subject was derived before this account existed on the meeting, so the
    // server keeps the stale one unless it is recomputed here.
    await this.meetingService.recomputeSecInfoSubjectAfterAttach(meeting);
  }

  /**
   * Sec-info subjects are derived from fields the create response settles, so the
   * subject computed for the payload can be stale by the time the record exists.
   */
  private async reconcileSubject(meeting: any): Promise<void> {
    const createFields = meeting['createRequestFields'] || {
      subject: meeting.subject,
      location: meeting.location,
      scheduledStart: meeting.scheduledStart,
      scheduledEnd: meeting.scheduledEnd,
    };
    meeting.subject = await this.meetingService.setMeetingSubject(meeting);

    const skipPatch = shouldSkipPostCreateMeetingPatch({
      isOffline: this.device.isOffline,
      hasOfflineData: this.activityService.hasOfflineMeetingData(meeting.ID),
      isAppointmentSecInfoConfigured: this.secondaryInfoService.isAppointmentSecInfoConfigured,
      createFields,
      finalFields: {
        subject: meeting.subject,
        location: meeting.location,
        scheduledStart: meeting.scheduledStart,
        scheduledEnd: meeting.scheduledEnd,
      },
    });

    if (!skipPatch && !this.device.isOffline && !this.activityService.hasOfflineMeetingData(meeting.ID)
      && this.secondaryInfoService.isAppointmentSecInfoConfigured) {
      await this.meetingService.updateMeeting(meeting as AppointmentActivity,
        new UpdateMeetingPayload(meeting.subject, meeting.location, meeting.scheduledStart, meeting.scheduledEnd));
    }

    if (!meeting['isCreatePendingSync'] || !skipPatch) {
      await this.activityService.upsertMeetingsOfflineData(meeting as AppointmentActivity);
    }
  }

  /* Parked for scope 1 — see goToDetailPaneInActivitiesTool.
  private async goToDetailPane(meeting: any, displacedActivity: any): Promise<QuickCreateResult> {
    this.activityService.isNewActivityFlow = true;
    const masterPage = this.navService.getCurrentMasterPageName();
    const currentPage = this.navService.getCurrentPageName();

    if (TOOLS_WITHOUT_A_RIGHT_PANE.has(masterPage) || TOOLS_WITHOUT_A_RIGHT_PANE.has(currentPage)) {
      return this.goToDetailPaneInActivitiesTool(meeting);
    }

    // Parked while the mobile right-pane work is in flight, so a second tap stacks a
    // second pane as before. Re-enable by restoring the condition below: a second tap
    // then replaces our open pane instead. Only a pushed pane is ours — tools that set
    // it as their root leave nothing to go back to, hence the canChildNavGoBack check.
    // const replacingOwnPane = this.navService.getActiveChildNavViewPageName() === PageName.ActivitiesDetailsPaneComponent
    //   && await this.navService.canChildNavGoBack();
    const replacingOwnPane = false;

    // Replacing must hand back what the tool had before the *first* meeting, not the
    // meeting that pane is showing. A pane closed in between resets this.
    const restore = replacingOwnPane && this._paneRestoreContext
      ? this._paneRestoreContext
      : {
        activeView: this.uiService.activeView,
        activity: displacedActivity,
        // Two independent flags: tools read one or the other (Account reads the child-nav
        // one, Activities the global one), so each has to be captured from its own source.
        showRightPane: this.uiService.showRightPane,
        // Normalised: tools that call initRightPaneChildNav without the flag leave it
        // undefined, and an undefined here would skip the restore and strand the pane open.
        childNavRightPane: !!this.navService.isActiveChildNavRightPaneViewDisplayed,
      };
    this._paneRestoreContext = restore;

    if (replacingOwnPane) {
      await this.navService.popChildNavPageWithPageTracking();
    }

    // Tools name their registered nav's master page independently of their own page name
    // (Survey registers PageName.SurveyComponent from SurveyPageComponent), so match either.
    const gatedActiveView = ACTIVE_VIEW_GATED_TOOLS.get(masterPage) ?? ACTIVE_VIEW_GATED_TOOLS.get(currentPage) ?? null;
    if (gatedActiveView) this.uiService.activeView = gatedActiveView;

    // On a phone the right pane is hidden until the rep opens something, so creating has
    // to reveal it exactly as tapping a list item does. Both flags, because the shell
    // reads either (home.ts showRightPane getter).
    this.uiService.showRightPane = true;
    this.navService.setChildNavRightPaneView(true);

    try {
      await this.navService.pushChildNavPageWithPageTracking(
        PageName.ActivitiesDetailsPaneComponent,
        PageName.ActivitiesDetailsPaneComponent,
        masterPage,
        {
          from: masterPage,
          isNewlyCreatedActivity: true,
          returnToUnderlyingPage: true,
          restoreOnClose: {
            // Always, not just for gated tools: a tool's right pane is often selected by
            // activeView (Account uses 'accountDetails'), and the pane's own close path
            // restores it per tool — which our early return skips.
            activeView: restore.activeView,
            activity: restore.activity,
            showRightPane: restore.showRightPane,
            childNavRightPane: restore.childNavRightPane,
          },
        },
      );
    } catch (error) {
      // Rejects with 'Invalid child nav controller' when the tool has no right-pane nav
      // at all. Nothing to host the pane here, so open it in the Activities tool.
      console.error('ActivityCreationService: detail pane push failed', error);
      this.uiService.activeView = restore.activeView;
      this.uiService.showRightPane = restore.showRightPane;
      this.navService.setChildNavRightPaneView(restore.childNavRightPane);
      return this.goToDetailPaneInActivitiesTool(meeting);
    }
    return { created: true, activity: meeting };
  }
  */

  /**
   * Opens the record in the right pane of the tool the rep is already in, rather than
   * re-rooting to Activities. showCancelDoneOnActivityDetails is what makes the pane behave
   * as a layover it can return from — without it `from` still sets isLayoverPushView and
   * markNewActivityOkay pops nothing (risk 6 in the tracker).
   */
  private async goToDetailPaneInHost(meeting: any, host: PageName): Promise<QuickCreateResult> {
    this.activityService.selectedActivity = meeting;
    this.uiService.showNewActivity = false;
    this.uiService.showCancelDoneOnActivityDetails = true;
    this.uiService.showRightPane = true;
    this.navService.setChildNavRightPaneView(true);
    this.footerService.initButtons(FooterViews.Activities);

    try {
      await this.navService.pushChildNavPageWithPageTracking(
        PageName.ActivitiesDetailsPaneComponent,
        PageName.ActivitiesDetailsPaneComponent,
        host,
        // ActivitiesDetailsPaneComponent pops on 'AccountTool'; it has no token for the
        // contact tool, which keeps the host name it has always been given there.
        { from: host === PageName.AccountPageComponent ? 'AccountTool' : host, isNewlyCreatedActivity: true },
      );
    } catch (error) {
      // Rejects with 'Invalid child nav controller' when the tool has no right-pane nav.
      console.error('ActivityCreationService: host detail pane push failed', error);
      return this.goToDetailPaneInActivitiesTool(meeting);
    }
    return { created: true, activity: meeting };
  }

  /** The phone call's equivalent of goToDetailPaneInHost; a different pane and footer. */
  private async goToPhoneCallDetailPaneInHost(
    phoneCall: PhoneActivity, host: PageName, restoreView?: string,
  ): Promise<QuickCreateResult> {
    this.activityService.selectedActivity = phoneCall;
    if (restoreView) this.uiService.prevView = restoreView;
    this.uiService.activeView = 'phoneCallTimelineDetails';
    this.uiService.showCancelDoneOnActivityDetails = true;
    this.uiService.showRightPane = true;
    this.navService.setChildNavRightPaneView(true);
    this.footerService.initButtons(FooterViews.PHONE_CALL);

    try {
      await this.navService.pushChildNavPageWithPageTracking(
        PageName.PhoneCallDetailsComponent,
        PageName.PhoneCallDetailsComponent,
        host,
        { from: host, isNewlyCreatedActivity: true },
      );
    } catch (error) {
      console.error('ActivityCreationService: host phone call pane push failed', error);
      return this.fail('ERROR_TRYAGAIN');
    }
    return { created: true, activity: phoneCall };
  }

  // Callers reveal the pane only after their page is up: setChildNavRoot records the page
  // before Ionic draws it, and a pane revealed off that record shows NothingSelectedView.
  private async enterActivitiesTool(): Promise<void> {
    if (this.navService.getCurrentPageName() === PageName.GoAppActivitiesPageComponent) return;
    await this.navService.setRootWithTracking(
      PageName.GoAppActivitiesPageComponent,
      PageName.GoAppActivitiesPageComponent,
      { __source: 'dashboard' },
      PageName.GoAppActivitiesPageComponent,
    );
    // The rail and footer keep the tool we left highlighted (none, from Notifications) until told.
    this.events.publish('setSelectedNavView', 'dashboard');
    this.events.publish('footer:selectedViewChanged', 'dashboard');
    this.uiService.showRightPane = false;
  }

  private async goToDetailPaneInActivitiesTool(meeting: any): Promise<QuickCreateResult> {
    this.activityService.isNewActivityFlow = true;
    this._paneRestoreContext = null;
    await this.enterActivitiesTool();
    // The root switch tears down the tool we came from, which clears selectedActivity —
    // and the detail pane reads it in its constructor.
    this.activityService.selectedActivity = meeting;
    this.uiService.activeView = 'ActivitiesPageRightPaneNav';
    await this.navService.setChildNavRoot(
      PageName.ActivitiesDetailsPaneComponent,
      PageName.ActivitiesDetailsPaneComponent,
      PageName.GoAppActivitiesPageComponent,
      { from: PageName.GoAppHomeComponent, isNewlyCreatedActivity: true },
    );
    this.uiService.showRightPane = true;
    return { created: true, activity: meeting };
  }

  private fail(reasonKey: string, style: ToastStyle = ToastStyle.DANGER): QuickCreateResult {
    this.notificationService.notify(this.translate.instant(reasonKey), 'New Activity', 'top', style);
    return REFUSED(reasonKey);
  }
}
